import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskCategory } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { useCreateTask, useSuggestions, type PlanSuggestion } from './queries';

/**
 * What a planner would say to do next.
 *
 * Distinct from the checklist above it, which is a template: this reads what the
 * couple actually has — how many guests against their target, what is booked,
 * what is late — and says what that means. The template knows that venues get
 * booked a year out; this knows that *you* are fourteen months out with two
 * shortlisted and neither booked.
 *
 * Each one is applied one at a time. There is no "add all": a suggestion the
 * couple disagrees with should cost them one press to ignore, not one press to
 * undo.
 */
export function Suggestions({ wedId, hasDate }: { wedId: string; hasDate: boolean }) {
  const { t } = useTranslation();
  const suggest = useSuggestions(wedId);
  const create = useCreateTask(wedId);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const add = (s: PlanSuggestion) => {
    create.mutate(
      {
        title: s.title,
        category: s.category as TaskCategory,
        ...(s.dueDate ? { dueDate: s.dueDate } : {}),
      },
      { onSuccess: () => setAdded((prev) => new Set(prev).add(s.title)) },
    );
  };

  const shown = (suggest.data?.suggestions ?? []).filter((s) => !dismissed.has(s.title));

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section">{t('suggest.title')}</h2>
        <Button
          variant="secondary"
          loading={suggest.isPending}
          disabled={!hasDate}
          onClick={() => {
            setDismissed(new Set());
            setAdded(new Set());
            suggest.mutate();
          }}
        >
          {suggest.data ? t('suggest.again') : t('suggest.ask')}
        </Button>
      </div>

      {!suggest.data && !suggest.isError && (
        <p className="max-w-prose text-caption text-leaf-soft">
          {hasDate ? t('suggest.hint') : t('suggest.needDate')}
        </p>
      )}

      {suggest.data?.note && <p className="max-w-prose text-body">{suggest.data.note}</p>}

      {shown.length > 0 && (
        <ul className="flex flex-col divide-y divide-line">
          {shown.map((s) => (
            <li key={s.title} className="flex flex-col gap-2 py-3">
              <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-body">{s.title}</span>
                {s.dueDate && (
                  <span className="tabular text-caption text-leaf-soft">{s.dueDate}</span>
                )}
                <span className="text-caption text-leaf-soft">
                  {t(`taskCategories.${s.category}`)}
                </span>
              </span>
              {/* The reasoning, not just the instruction — it is the whole
                  difference between this and the template. */}
              <span className="max-w-prose text-caption text-leaf-soft">{s.why}</span>
              <span className="flex gap-3">
                {added.has(s.title) ? (
                  <span className="text-caption text-leaf">{t('suggest.added')}</span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => add(s)}
                      className="min-h-tap text-caption text-brass-text underline underline-offset-4"
                    >
                      {t('suggest.add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDismissed((prev) => new Set(prev).add(s.title))}
                      className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
                    >
                      {t('suggest.dismiss')}
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {suggest.isError && (
        <p role="alert" className="text-caption text-status-declinedFg">
          {suggest.error.message}
        </p>
      )}
      {suggest.data && shown.length === 0 && !suggest.data.note && (
        <p className="text-caption text-leaf-soft">{t('suggest.nothing')}</p>
      )}
    </section>
  );
}
